import { useState } from 'react'
import buildLogo from '@/assets/illustrations/unimobuild logo.svg'
import liveLogo from '@/assets/illustrations/unimolive logo.svg'
import reportLogo from '@/assets/illustrations/unimioreportlogo.svg'

// Logos are the same ones ProductsSection uses on the homepage carousel
// (src/features/homepage/components/ProductsSection.tsx) — reused, not
// re-exported or duplicated.
const PRODUCTS = [
    {
        key: 'unimoBuild',
        logo: buildLogo,
        body: 'unimoBuild is an AI-driven digital twin tool that creates accurate, living 3D models in minutes. Import PDFs, screenshots, photos, IFC exports from Navisworks or Revit and just ask it what you need. No specialist team or lengthy training needed. Install it, open it, start building.',
    },
    {
        key: 'unimoLive',
        logo: liveLogo,
        body: 'unimoLive is out of the box compatible with almost every sensor on the planet. It monitors weather, noise, power, and any other data your site produces. One view of everything, updating as it happens. When something changes on site, you can intervene before it becomes a bigger problem.',
    },
    {
        key: 'unimoReport',
        logo: reportLogo,
        body: 'unimoReport makes your data work for you, turning complex datasets into intelligence that is visualised, actionable, and ready to use. Pull in your unimoLive data or connect your own from tools like Microsoft Fabric. Build custom dashboards and stay compliant with on-demand reporting.',
    },
]

// Tailwind's own `md` breakpoint (768px) — matches every other mobile/desktop
// split already on this page (card grid, headshot sizing, etc.), so the
// accordion's default-open state uses the same cutoff rather than a new one.
const DESKTOP_QUERY = '(min-width: 768px)'

// Independent expand/collapse per row (not a single-open accordion) — several
// can be open at once, per Charly. Chevron markup/rotation and the
// aria-expanded pattern are copied from the existing nav dropdowns
// (NavIndustriesDropdown/NavResourcesDropdown), not invented fresh. Default
// state is breakpoint-dependent per Charly: open on desktop, closed on
// mobile — read once at mount, not kept in sync on later resize (a visitor
// changing device width mid-visit is an edge case not worth overriding their
// own manual toggles for).
function ProductRow({ product }: { product: (typeof PRODUCTS)[number] }) {
    const [isOpen, setIsOpen] = useState(() => typeof window !== 'undefined' && window.matchMedia(DESKTOP_QUERY).matches)
    const panelId = `ukcw-product-panel-${product.key}`

    return (
        <div className="border-b border-unimo-site-grey-300">
            <button
                type="button"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setIsOpen((open) => !open)}
                className="flex w-full items-center justify-between gap-4 py-4 text-left"
            >
                {/* Logo carries the product name as part of the wordmark itself
                    (same asset ProductsSection uses on the homepage, logo-only,
                    name in `alt` not a separate visible label) — a second text
                    label here would just repeat what the logo already shows. */}
                <img src={product.logo} alt={product.key} className="h-6 w-auto" />
                {/* 50%+ larger on desktop (16px → 24px) per Charly — mobile unchanged. */}
                <svg
                    aria-hidden="true"
                    className={`h-4 w-4 shrink-0 text-unimo-site-turquoise transition-transform md:h-6 md:w-6 ${isOpen ? 'rotate-180' : ''}`}
                    viewBox="0 0 20 20"
                    fill="none"
                >
                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
            {isOpen && (
                <p id={panelId} className="pb-4 font-light text-unimo-slate">
                    {product.body}
                </p>
            )}
        </div>
    )
}

// Heading and lead line: px-8 on mobile (matching the hero's own text inset
// there), flush left from md: up, same as ExpertCards/OnTheDay — lines up
// with the hero image/card grid's left edge on desktop. Lead line stays
// visible at all times, unchanged, above the accordion. The accordion list
// itself is narrower than the heading/lead line above it — two-thirds width
// on desktop, still left-aligned at the same edge, per Charly. The three
// product rows are closed by default; body copy inside each is unchanged
// from the original page, verbatim.
export function WhoWeAre() {
    return (
        <section className="px-8 pb-section-gap pt-section-gap md:px-0">
            <h2 className="pb-section-headline-gap-bottom text-2xl font-medium text-unimo-slate lg:text-3xl">
                What is unimo?
            </h2>
            <p className="text-lg font-medium text-unimo-slate lg:text-xl">
                Model, watch, and report on your site. No data engineers, no analysts. Just ask.
            </p>
            <div className="mt-6 md:w-2/3">
                {PRODUCTS.map((product) => (
                    <ProductRow key={product.key} product={product} />
                ))}
            </div>
        </section>
    )
}

export default WhoWeAre
